import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { shallow } from 'zustand/shallow';

import { BaseNodeForm } from '../base-node-form';

import { RatingDialogueBlock } from './rating-dialog-block';
import { RatingScaleBlock } from './rating-scale-block';
import { RetryOptionsBlock } from './retry-options-block';

import { NumericRatingBotModuleData } from '@/domain/bot';
import { NodeFormProps } from '@/domain/node-form-props';
import { useTelmaPortalStore } from '@/store/telma-portal-store';

export const botNumericRatingNodeSchema = yup.object().shape({
    max: yup
        .number()
        .required('value_required')
        .min(1, 'must_be_min_1')
        .max(10, 'must_be_max_10')
        .typeError('numbers_only')
        .integer(),
    min: yup
        .number()
        .required('value_required')
        .typeError('numbers_only')
        .min(1, 'must_be_min_1')
        .max(10, 'must_be_max_10')
        .integer(),
    retries: yup
        .number()
        .required('value_required')
        .typeError('numbers_only')
        .min(0)
        .integer(),
    prompt: yup.string().max(1000, 'too_long'),
    out_of_bounds_prompt: yup.string().max(1000, 'too_long'),
    not_found_prompt: yup.string().max(1000, 'too_long'),
    found_prompt: yup.string().max(1000, 'too_long'),
    try_again_prompt: yup.string().max(1000, 'too_long'),
    max_retries_reached_prompt: yup.string().max(1000, 'too_long'),
});

export const NumericRatingNodeForm: FC<NodeFormProps> = ({ nodeIndex }) => {
    const [setNode, node, onValidationChange] = useTelmaPortalStore(
        (state) => [
            state.setNode,
            state.editingBot.nodesReactFlow[nodeIndex],
            state.onValidationChange,
        ],
        shallow,
    );

    const {
        handleSubmit,
        control,
        formState: { isValid, errors },
    } = useForm<NumericRatingBotModuleData['params']>({
        resolver: yupResolver(botNumericRatingNodeSchema),
        //@ts-ignore - data type problem
        defaultValues: node?.data?.params,
    });

    const changeValid = useCallback(() => {
        onValidationChange(!!isValid, nodeIndex);
    }, [isValid, nodeIndex, onValidationChange]);

    useEffect(() => {
        changeValid();
    }, [changeValid, isValid]);
    const botNodeSubmit: SubmitHandler<NumericRatingBotModuleData['params']> = (
        formData,
    ) => {
        if (isValid) {
            setNode(nodeIndex, formData);
        }
    };

    return (
        <BaseNodeForm nodeId={node.id} nodeTitle={node.type}>
            <form onBlur={handleSubmit(botNodeSubmit)}>
                <div className='grid'>
                    <RatingScaleBlock nodeIndex={nodeIndex} control={control} />

                    <RatingDialogueBlock
                        nodeIndex={nodeIndex}
                        control={control}
                    />

                    <RetryOptionsBlock
                        nodeIndex={nodeIndex}
                        control={control}
                    />
                </div>
            </form>
        </BaseNodeForm>
    );
};
